<template>
    <div class="redesign-extension-card-header">
        <div class="flex justify-between mb-2">
            <div class="flex items-center" :class="{'w-4/6': cardsCols > 4}">
                <redesign-tooltip placement="top" effect="customized" popper-class="custom" :visible-arrow="false">
                  <template #tooltip-content>
                    {{ statusText }}
                  </template>
                    <div
                        key="icon"
                        :style="{backgroundColor: statusIcon.color }"
                        class="p-1 redesign-extension-card-header__icon"
                    >
                      <redesign-base-icon
                          :name="statusIcon.icon"
                          :fill="statusIcon.color"
                          :size="agentNameFontSize"
                          :color="'white'"
                      />
                    </div>
                </redesign-tooltip>
                <div
                    key="text"
                    class="text-nowrap mx-2 font-bold text-steel redesign-extension-card-header__title"
                    :style="representativeNameStyling"
                >
                    {{ representativeUserName }}
                </div>
            </div>
            <div>
                <redesign-toggle-button
                    v-if="showStatistics"
                    @toggle="(val) => emit('toggleSummary', val)"
                    bgColor="blue"
                    borderRadius="18px"
                    size="xs"
                    :on-label="summaryText"
                    :off-label="summaryText"
                    type="primary"
                    :round="true"
                    :disabled="false"
                />
            </div>
        </div>
        <div
            class="flex justify-between redesign-extension-card-header__content"
            :class="{'redesign-extension-card-header__content--hidden': showSummaryRow}"
        >
            <div :style="{color: statusColor}" class="uppercase text-3xl font-bold gap-4 flex items-center" :class="{'gap-0-25': cardsCols > 4}">
                <div style="font-variant-numeric: tabular-nums;">
                    {{ timerDisplayTime }}
                </div>
                <div v-show="statusColor"  class="p-0-5 rounded-full" :style="{background: iconTimerFillColor}">
                    <redesign-base-icon :fill="iconTimerFillColor" name="IconCircleAlert" :size="30"/>
                </div>

            </div>
            <div class="flex gap-1 items-center" v-if="showStatistics && statistic.length">
                <div v-for="(tag, index) in statistic">
                    <redesign-tooltip>
                        <template #tooltip-content>
                            <div
                                v-for="(item, i) in tag.fullSummary"
                                :key="'fullSummary' + i"
                                :class="{'mb-2': tag.fullSummary.length > 1}"
                            >
                                <div class="uppercase text-main-sm font-medium mb-1">
                                    {{ item.label }}
                                </div>
                                <div class="flex gap-2">
                                    <div class="flex gap-1 items-center" v-for="value in item.values">
                                        <redesign-base-icon
                                            :name="value.icon"
                                            :color="value.iconColor"
                                        />
                                        <div class="text-sm">
                                            {{ value.value }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <redesign-tag
                            v-show="tag.showAlways"
                            :color="tag.color"
                            :tag-content="tag.tagContent"
                            :key="index"
                            :border-color="tag.borderColor"
                            :padding-classes="'py-1.5 px-2'"
                            outline
                            class="cursor-pointer text-sm"
                        >
                        </redesign-tag>
                    </redesign-tooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, watch, ref} from 'vue';
import { useTimer } from "@/composables/useTimer";
import RedesignBaseIcon from "@/components/RedesignBaseComponents/RedesignBaseIcon.vue";
import RedesignTag from "@/components/RedesignBaseComponents/RedesignTag.vue";
import RedesignToggleButton from "@/components/RedesignBaseComponents/RedesignToggleButton.vue";
import redesignStatusTypes from "@/enum/redesignStatusTypes";
import { redesignCallTypeIcons } from "@/enum/colors";
import store from "@/store/store";
import { useExtensionCardInject } from "@/composables/useExtensionCardProvideInject";
import i18n from "@/i18n";
import RedesignTooltip from "@/components/RedesignBaseComponents/RedesignTooltip.vue";
import get from "lodash/get";
import { realTimeSettings } from '@/enum/defaultWidgetSettings'
const summaryText = i18n.t('widget.summary')

//TODO ADD TO LAYOUT IN FUTURE
const RATIO_POSITIVE_COLOR = '#0D710D'
const RATIO_NEGATIVE_COLOR = '#FF4D4D'

const props = defineProps({
    cardsCols: Number,
    settings: {
        type: Object,
        default: () => ({})
    },
    extension: {
        type: Object,
        default: () => ({})
    },
    showSummaryRow: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['toggleSummary', 'statusColorChanged']);
const extension = useExtensionCardInject()
const statusColor = ref('')

const { timerDisplayTime, startTimer, timerState } = useTimer()

const limitsColors = computed(() => {
    const { loginStatusLimit, loginStatusWarning } = store.getters['layout/colors']('activeLayout')
    return {
        loginStatusWarning,
        loginStatusLimit,
    }
})

/* Computed */
const agentNameFontSize = computed(() => {
    return get(props.settings, 'agentNameFontSize', realTimeSettings.agentNameFontSize)
})
const representativeNameStyling = computed(() => {
    return {
        fontSize: `${agentNameFontSize.value}px`,
        lineHeight: `${agentNameFontSize.value}px`
    }
})
const showStatistics = computed(() => {
    return !props.settings.hideStatistics
})
const statusLimits = computed(() => {
    return store.getters['entities/getAccountBreakLimit'](extension.value.representativeStatus)
})
const representativeUserName = computed(() => {
    return get(extension.value, 'summery.representative', extension.value.userName)
})

const iconTimerFillColor = computed(() => {
    if (statusColor.value) {
        return statusColor.value + '19'
    }
})
const statusIcon = computed(() => {
    let data;

    if (extension.value.calls.length > 0) {
        data = redesignStatusTypes.value['100'];
    } else {
        data = redesignStatusTypes.value[extension.value.representativeStatus] || { icon: 'IconOther', color: '#default' };
    }

    const dynamicColor = accountStatuses.value.find(el => Number(el.StatusID) === Number(data.value));
    const color = dynamicColor && dynamicColor.ColorCode ? dynamicColor.ColorCode : data.color;

    return {
        icon: data.icon,
        color: color
    };
});

const statusText = computed(() => {
  let text = store.getters['entities/getStatusTextById'](extension.value.representativeStatus)
  if (isTalking.value) {
    text = 'status.talking'
  }
  if (isCalling.value) {
    text = 'status.calling'
  }
  return i18n.t(text)
})

const isCalling = computed(() => {
  if (extension.value.calls.length === 0) {
    return false
  }
  return extension.value.calls.every(c => c.callAnswered === 0)
})

const isTalking = computed(() => {
  if (extension.value.calls.length === 0) {
    return false
  }
  return extension.value.calls.every(c => c.callAnswered !== 0);
})

const statistic = computed(() => {
    const extensionStatistic = extension.value.allDataSummary

    if (extensionStatistic) {
        const calls = {
            showAlways: true,
            tagContent: [{iconName: 'Phone', text: extensionStatistic.TotalCallsCount}],
            fullSummary: [
                getFullSummaryStatisticObject(i18n.t('widget.total.calls'), extensionStatistic.OutgouingCallsCount, extensionStatistic.IncomingCallsCount, extensionStatistic.OtherCallsCount),
            ]
        }

        const totalStatistic = {
            showAlways: props.cardsCols <= 3,
            tagContent: [{iconName: 'Gauge', text: extensionStatistic.TotalSum}, {
                iconName: 'IconCustomLineChart',
                text: extensionStatistic.AverageCallsPerHour
            }],

            fullSummary: [
                getFullSummaryStatisticObject(i18n.t('widget.total.calls'), extensionStatistic.OutgouingCallsSum, extensionStatistic.IncomingCallsSum),
                getFullSummaryStatisticObject(i18n.t('statistics.avg.call.duration'), extensionStatistic.OutgouingCallsAvg, extensionStatistic.IncomingCallsAVG)
            ]

        }

        const rate = {
            color: extensionStatistic.GrossPercentageOfEmployment >= props.settings.positivePerformanceThreshold ? RATIO_POSITIVE_COLOR : RATIO_NEGATIVE_COLOR,
            borderColor: extensionStatistic.GrossPercentageOfEmployment >= props.settings.positivePerformanceThreshold ? RATIO_POSITIVE_COLOR : RATIO_NEGATIVE_COLOR,
            showAlways: true,
            tagContent: [{iconName: 'Star', text: String(extensionStatistic.GrossPercentageOfEmployment)}],
            fullSummary: [
                {
                    label: i18n.t('statistic.avg.ratio'),
                }
            ]
        }
        return [calls, totalStatistic, rate]
    }
    return []
})

const accountStatuses = computed(() => {
    return store.getters["entities/accountStatuses"]
})
/*methods*/
const getFullSummaryStatisticObject = (label, outgoingVal, incomingVal, otherVal) => {
  const statisticObject = {
    label,
    values: [
      {
        icon: redesignCallTypeIcons.outgoing,
        iconColor: redesignCallTypeIcons.outgoingColor,
        value: String(outgoingVal)
      },
      {
        icon: redesignCallTypeIcons.incoming,
        iconColor: redesignCallTypeIcons.incomingColor,
        value: String(incomingVal)
      }
    ]
  }
  if (otherVal || otherVal === 0) {
    statisticObject.values.push(
        {
          icon: redesignCallTypeIcons.otherCalls,
          iconColor: redesignCallTypeIcons.otherColor,
          value: String(otherVal)
        }
    )
  }
  return statisticObject
}

watch(() => timerState.value, (val) => {
    let currentStatusColor = ''
    if (statusLimits.value && val >= statusLimits.value.warn) {
        currentStatusColor = limitsColors.value.loginStatusWarning
    }
    if (statusLimits.value && val >= statusLimits.value.alert) {
        currentStatusColor = limitsColors.value.loginStatusLimit
    }
    if (statusColor.value !== currentStatusColor) {
        statusColor.value = currentStatusColor
        emit('statusColorChanged', statusColor.value);
    }
})
startTimer(extension, props.settings)
</script>

<style lang="scss">
.redesign-extension-card-header {
    @apply px-3 pt-3 pb-2;
    border-bottom: 1px solid var(--colorWidgetBorder-color);
    &__icon {
        border-radius: 4px;
    }

    &__title {
        color: var(--mainTextColor-color);
    }

    .text-nowrap {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    &__content {
        overflow: hidden;
        transition: max-height .5s ease-out, opacity .5s ease-out;
        max-height: 80px;
        opacity: 1;
        will-change: max-height, opacity;

        &--hidden {
            max-height: 0;
            opacity: 0;
        }
    }
}
</style>
